import { Languages, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { LANGUAGE_LABELS } from '@klareospace/pharos-lib-front-utils';
import { SUPPORTED_LANGUAGES } from '@klareospace/pharos-lib-types';
import type { SupportedLanguage } from '@klareospace/pharos-lib-types';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { SidebarUserCard } from '../sidebar-user-card';

export interface SidebarUserMenuProps {
  currentLanguage: SupportedLanguage;
  displayName?: string;
  email?: string;
  languages: SupportedLanguage[];
  onLanguageChange: (lang: SupportedLanguage) => void;
  onSignOut: () => void;
  /** Build label (e.g. `v1.1.0 - 5a17cfb`) shown below sign-out. Omitted entirely when absent. */
  versionLabel?: string;
}

function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

export function SidebarUserMenu({
  currentLanguage,
  displayName = '',
  email = '',
  languages,
  onLanguageChange,
  onSignOut,
  versionLabel,
}: SidebarUserMenuProps): React.JSX.Element {
  const { t } = useTranslation();
  const ariaLabel = displayName ? `Open user menu for ${displayName}` : 'Open user menu';

  function handleLanguageChange(value: string): void {
    if (isSupportedLanguage(value)) {
      onLanguageChange(value);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarUserCard
          aria-label={ariaLabel}
          avatarClassName="bg-blue-500"
          fallbackInitials="??"
          name={displayName}
          subtitle={email}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" side="top">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages className="me-2 size-4" />
            {t('auth.language')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={currentLanguage} onValueChange={handleLanguageChange}>
              {languages.map((lang) => (
                <DropdownMenuRadioItem key={lang} value={lang}>
                  {LANGUAGE_LABELS[lang]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={(): void => void onSignOut()}>
          <LogOut className="me-2 size-4" />
          {t('auth.signOut')}
        </DropdownMenuItem>
        {versionLabel && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="px-2 py-1 text-center text-[11px] font-normal text-muted-foreground">
              {versionLabel}
            </DropdownMenuLabel>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
